import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney, priceData, subUnitsToMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  AvatarSmall,
  ContractLeaseActions,
  ContractLeaseData,
  IconVerified,
  ListingStatus,
  NamedLink,
  ResponsiveImage,
} from '../../components';
import { LISTING_INTENT_TYPE, LISTING_STATUS } from '../../constants/listing';
import { isMarketplaceRespare, isMarketplaceSecondLife } from '../../util/marketplace';
import { useLocation } from 'react-router-dom';
import ListingFlag from '../ListingFlag/ListingFlag';
import {
  getListingFlag,
  getNumberOfBuyingRequests,
  hasAnyBuyingRequest,
  isEndingSoon,
  isExpired,
} from '../../util/listing';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const MODAL_BREAKPOINT = 768;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    isListView,
    isAuthenticated,
  } = props;

  const isWindowDefined = typeof window !== 'undefined';
  const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;

  const location = useLocation();
  const history = useHistory();
  const currentPath = history?.location?.pathname;

  const isSellAsIsPage = currentPath.includes('sell-as-is');

  const is2ndLifeMarketplace = isMarketplaceSecondLife(config);

  const classes = classNames(
    rootClassName || css.root,
    { [css.listView]: isListView },
    { [css.listingCard2ndLife]: is2ndLifeMarketplace },
    className
  );
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData, metadata } = currentListing.attributes;
  const { status } = metadata || {};
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const authorDisplayName = author.attributes.profile.publicData?.companyName || authorName;

  const {
    intentType,
    location: listingLocation,
    dimrProductImageStudio,
    husqvarnaCertified,
    residualValue,
    locale,
  } = publicData || {};

  const isRequesting = intentType === LISTING_INTENT_TYPE.REQUESTING;

  const isListingExpired = isExpired(currentListing);
  const isListingEndingSoon = isEndingSoon(currentListing);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const currency = price?.currency || config.currency;
  const { formattedPrice, priceTitle } = priceData(price, currency, intl);

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const imageOverlays = (
    <>
      {is2ndLifeMarketplace && (isListingExpired || isListingEndingSoon) && (
        <ListingFlag
          className={css.listViewListingFlag}
          position="bottomRight"
          flag={getListingFlag(currentListing)}
          intl={intl}
        />
      )}

      {is2ndLifeMarketplace && hasAnyBuyingRequest(currentListing) && (
        <ListingFlag
          className={css.listViewListingFlag}
          position="bottomRight"
          flag={getNumberOfBuyingRequests(currentListing) > 1 ? 'buyingRequests' : 'buyingRequest'}
          numberOfBuyingRequests={getNumberOfBuyingRequests(currentListing)}
          intl={intl}
        />
      )}

      {is2ndLifeMarketplace && isAuthenticated && (
        <ListingStatus
          className={css.listViewListingStatus}
          position="topRight"
          listing={currentListing}
          isSellAsIsPage={isSellAsIsPage}
        />
      )}

      {is2ndLifeMarketplace &&
        (status === LISTING_STATUS.REFURBISHED ||
          status === LISTING_STATUS.SOLD ||
          status === LISTING_STATUS.PUBLISHED) &&
        husqvarnaCertified && (
          <div className={css.listViewHusqvarnaCertified}>
            <IconVerified />
          </div>
        )}
    </>
  );

  return (
    <div className={classes} {...setActivePropsMaybe}>
      <NamedLink
        name="ListingPage"
        params={{ id, slug }}
        className={css.link}
        to={{
          state: {
            from: `${location.pathname}${location.search}`,
          },
        }}
      />
      {isListView ? (
        <div className={css.listImageColumn}>
          <img src={dimrProductImageStudio?.[0].Url} alt={title} className={css.listImage} />
          {imageOverlays}
        </div>
      ) : (
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          isListView={isListView}
        >
          {isMarketplaceRespare(config) && (firstImage || !dimrProductImageStudio?.length) ? (
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={firstImage}
              variants={variants}
              sizes={renderSizes}
            />
          ) : (
            isMarketplaceSecondLife(config) &&
            dimrProductImageStudio?.length > 0 && (
              <img
                src={dimrProductImageStudio?.[0].Url}
                width="100%"
                height="100%"
                alt={title}
                className={css.rootForImage}
              />
            )
          )}
          {is2ndLifeMarketplace && (isListingExpired || isListingEndingSoon) && (
            <ListingFlag
              className={isListView ? css.listViewlListingFlag : css.listingFlag}
              position={isListView ? 'bottomRight' : 'bottomLeft'}
              flag={getListingFlag(currentListing)}
              intl={intl}
            />
          )}
          {is2ndLifeMarketplace && hasAnyBuyingRequest(currentListing) && (
            <ListingFlag
              className={isListView ? css.listViewlListingFlag : css.listingFlag}
              position={isListView ? 'bottomRight' : 'bottomLeft'}
              flag={
                getNumberOfBuyingRequests(currentListing) > 1 ? 'buyingRequests' : 'buyingRequest'
              }
              numberOfBuyingRequests={getNumberOfBuyingRequests(currentListing)}
              intl={intl}
            />
          )}
          {is2ndLifeMarketplace && isAuthenticated && (
            <ListingStatus
              className={isListView ? css.listViewListingStatus : css.listingStatus}
              position={
                isListView
                  ? 'topRight'
                  : isListingExpired || isListingEndingSoon || hasAnyBuyingRequest(currentListing)
                  ? 'topLeft'
                  : 'bottomLeft'
              }
              listing={currentListing}
              isSellAsIsPage={isSellAsIsPage}
            />
          )}
          {is2ndLifeMarketplace &&
            (status === LISTING_STATUS.REFURBISHED ||
              status === LISTING_STATUS.SOLD ||
              status === LISTING_STATUS.PUBLISHED) &&
            husqvarnaCertified && (
              <div className={isListView ? css.listViewHusqvarnaCertified : css.husqvarnaCertified}>
                <IconVerified />
              </div>
            )}
        </AspectRatioWrapper>
      )}

      {!is2ndLifeMarketplace && (
        <div className={classNames(css.priceContainer, { [css.priceOverImage]: !isListView })}>
          <div className={css.priceWrapper}>
            {isRequesting ? (
              <FormattedMessage id="General.requesting" />
            ) : (
              <span title={priceTitle}>{formattedPrice}</span>
            )}
          </div>
        </div>
      )}
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {isListView ? <span className={css.location}>{listingLocation?.address}</span> : null}
          {is2ndLifeMarketplace && (
            <ContractLeaseData
              listing={currentListing}
              isListView={isListView}
              className={css.leaseData}
            />
          )}
          {is2ndLifeMarketplace && isAuthenticated && residualValue && locale && (
            <div className={css.residualValue}>
              {formatMoney(intl, subUnitsToMoney(residualValue, locale === 'no' ? 'NOK' : 'SEK'))}
            </div>
          )}

          {is2ndLifeMarketplace && !isAuthenticated && price && (
            <div className={css.residualValue}>
              {formatMoney(intl, subUnitsToMoney(price?.amount, price?.currency || 'NOK'))}
            </div>
          )}

          {showAuthorInfo && author?.id && (
            <NamedLink
              name="ProfilePage"
              params={{ id: author.id.uuid }}
              className={css.authorInfo}
            >
              {isListView && (
                <AvatarSmall user={author} disableProfileLink className={css.avatar} />
              )}
              <FormattedMessage
                id="ListingCard.author"
                values={{ authorName: authorDisplayName }}
              />
            </NamedLink>
          )}
        </div>
        {is2ndLifeMarketplace && isListView && (
          <div className={classNames({ [css.hide]: isMobileLayout })}>
            <ContractLeaseData
              isSecondary
              isListView={isListView}
              listing={currentListing}
              className={css.leaseData}
            />
          </div>
        )}
        {is2ndLifeMarketplace && (
          <div
            className={classNames(css.leaseActionsWrapper, {
              [css.hide]: isMobileLayout,
              [css.listVariant]: isListView,
            })}
          >
            <ContractLeaseActions
              listing={listing}
              className={classNames(css.leaseActions, { [css.listVariant]: isListView })}
            />
          </div>
        )}
      </div>
    </div>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,
  isListView: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
